import { type ReactNode, useId } from 'react';
import { cx } from '@/lib/cx';
import s from './Field.module.css';

export interface FieldRenderProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
}

/** Label + control + hint/error, with the ids wired for assistive tech. */
export function Field({
  label,
  hint,
  error,
  required,
  className,
  children,
  inline,
}: {
  label: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  className?: string;
  inline?: boolean;
  children: (props: FieldRenderProps) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx(s.field, inline && s.inline, className)}>
      <label htmlFor={id} className={s.label}>
        {label}
        {required && (
          <span className={s.required} aria-hidden>
            *
          </span>
        )}
      </label>
      {children({ id, 'aria-describedby': describedBy, 'aria-invalid': error ? true : undefined })}
      {hint && !error && (
        <p id={hintId} className={s.hint}>
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className={s.error} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
